import { IMAGES } from "../constants/images";
import { useLocation } from 'react-router-dom';
import { use, useEffect, useState } from 'react';
import { ROUTES } from "../App";
import { ICONS } from "../constants/icons";
import { LuEyeOff as FaRegEyeSlash, LuEye as FaRegEye } from "react-icons/lu";
import { createUserWithEmailAndPassword, signInWithCredential, signOut } from "firebase/auth";
import { auth, database } from "../constants/firebase";
import { get, ref, set } from "firebase/database";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useUser } from "../contexts/UserContext";
import useMoveTo from "../constants/navigation";
import { createSession, saveToken } from "../utils/utils";
import { getNames } from 'country-list';
import Select from 'react-select';


// Country select styling to match your Niche select
const customSelectStyles = {
    control: (provided, state) => ({
        ...provided,
        borderRadius: '0.75rem', // rounded-xl
        padding: '0.75rem 1rem', // px-4 py-3
        backgroundColor: '#F5F9FE', // same as bg
        borderColor: state.isFocused ? '#E5E7EB' : '#E5E7EB', // gray-200 border
        boxShadow: 'none',
        minHeight: '48px',
        cursor: 'pointer',
    }),
    valueContainer: (provided) => ({
        ...provided,
        padding: 0,
    }),
    input: (provided) => ({
        ...provided,
        margin: 0,
        padding: 0,
        color: '#000',
    }),
    placeholder: (provided) => ({
        ...provided,
        color: '#7C8BA0', // placeholder color
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        padding: '0 0rem',
        color: '#000',
    }),
    indicatorSeparator: () => ({
        display: 'none',
    }),
    menu: (provided) => ({
        ...provided,
        backgroundColor: '#F5F9FE', // same background as select
        borderRadius: '0.75rem',
        marginTop: '4px',
        border: '1px solid #E5E7EB', // same border color
        zIndex: 50,
        overflow: 'hidden',
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isFocused ? '#E5E7EB' : '#F5F9FE', // hover color same as input border hover
        color: '#000',
        cursor: 'pointer',
        padding: '0.75rem 1rem',
    }),
};


const SignUp = () => {
    const navigate = useMoveTo();
    const location = useLocation();
    const [formData, setFormData] = useState({
        uid: '',
        name: '',
        email: '',
        password: '',
        username: '',
        about: '',
        niche: 'Filmmaker',
        location: '',
        country: '',
        agreeToTerms: false,
        isAdmin: false
    });
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [googleSignUp, setGoogleSignUp] = useState(false);
    const [errors, setErrors] = useState({});
    const { refreshUser, logout } = useUser();
    const [googleProvidedName, setGoogleProvidedName] = useState(false);
    const [googleProvidedEmail, setGoogleProvidedEmail] = useState(false);
    const [countryOptions, setCountryOptions] = useState([]);


    useEffect(() => {
        const countries = getNames().map(name => ({ value: name, label: name }));
        setCountryOptions(countries);
    }, []);

    // Initialize with location state
    useEffect(() => {
        if (location.state?.isGoogleSignUp) {
            setGoogleSignUp(true);
            setFormData(prev => ({
                ...prev,
                uid: location.state.uid || "",
                name: location.state.name || "",
                email: location.state.email || "",
                profilePic: location.state.profilePic || ""
            }));
            setGoogleProvidedName(!!location.state.name);
            setGoogleProvidedEmail(!!location.state.email);
        }
    }, [location.state]);

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setErrors({});

        const newErrors = {};
        if (!formData.name) newErrors.name = 'Name is required';
        if (!formData.email) newErrors.email = 'Email is required';
        if (!googleSignUp) {
            if (!formData.password) newErrors.password = 'Password is required';
        }
        if (!formData.username) newErrors.username = 'Username is required';
        if (!formData.niche) newErrors.niche = 'Niche is required';
        if (!formData.location) newErrors.location = 'Location is required';
        if (!formData.country) newErrors.country = 'Country is required';
        if (!formData.about) newErrors.about = 'About is required';
        if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            setIsSubmitting(false);
            return;
        }

        try {
            if (googleSignUp) {
                await set(ref(database, 'Users/' + formData.uid), {
                    uid: formData.uid,
                    name: formData.name,
                    username: formData.username,
                    about: formData.about,
                    email: formData.email,
                    niche: formData.niche,
                    location: formData.location,
                    country: formData.country,
                    role: 'Free',
                    provider: "google",
                    isTeamUser: false,
                    createdAt: new Date().toISOString(),
                    isAdmin: false,
                });
                localStorage.setItem("uid", formData.uid);
                await refreshUser();
                // ✅ create new sessionId (kicks old session)
                await createSession(formData);
                // ✅ save FCM token only for this session
                await saveToken(formData);
                // 👇 Send message to React Native app if inside WebView
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "USER_LOGGED_IN",
                            uid: formData.uid,
                            email: formData.email,
                        })
                    );
                }
                navigate(ROUTES.DASHBOARD, { replace: true });
            } else {
                const userCredential = await createUserWithEmailAndPassword(auth, formData.email, formData.password);
                const user = userCredential.user;

                await set(ref(database, 'Users/' + user.uid), {
                    uid: user.uid,
                    name: formData.name,
                    username: formData.username,
                    about: formData.about,
                    email: user.email,
                    password: formData.password,
                    niche: formData.niche,
                    location: formData.location,
                    country: formData.country,
                    role: 'Free',
                    isTeamUser: false,
                    createdAt: new Date().toISOString(),
                    isAdmin: false,
                });

                localStorage.setItem("uid", user.uid);
                await refreshUser();
                // ✅ create new sessionId (kicks old session)
                await createSession(user);
                // ✅ save FCM token only for this session
                await saveToken(user);
                // 👇 Send message to React Native app if inside WebView
                if (window.isInApp) {
                    window.ReactNativeWebView.postMessage(
                        JSON.stringify({
                            type: "USER_LOGGED_IN",
                            uid: user.uid,
                            email: user.email,
                        })
                    );
                }
                navigate(ROUTES.DASHBOARD, { replace: true });
            }
        } catch (error) {
            console.error("Firebase sign up error:", error.message);
            setErrors({ firebase: error.message });
        } finally {
            setIsSubmitting(false);
        }

    };


    const handleGoogleSignUp = async () => {
        if (window.isInApp) {
            // Inside React Native WebView → request native login
            window.ReactNativeWebView.postMessage(
                JSON.stringify({ type: "GOOGLE_LOGIN" })
            );
        } else {
            // Normal Web login
            const provider = new GoogleAuthProvider();
            try {
                const result = await signInWithPopup(auth, provider);
                await processFirebaseUser(result.user);
            } catch (err) {
                console.error("Google login failed:", err.message);
            }
        }
    };

    // Listen for native Google login result from WebView
    useEffect(() => {
        const handleMessage = async (event) => {
            try {
                const message = JSON.parse(event.data);

                if (message.type === "GOOGLE_LOGIN_SUCCESS") {
                    const credential = GoogleAuthProvider.credential(message.token);
                    const result = await signInWithCredential(auth, credential);
                    await processFirebaseUser(result.user);
                }

                if (message.type === "GOOGLE_LOGIN_ERROR") {
                    console.error("Native Google login failed:", message.error);
                }
            } catch { }
        };

        if (window.isInApp) {
            document.addEventListener('message', handleMessage);  // Android
            window.addEventListener('message', handleMessage);  // iOS
            return () => {
                document.removeEventListener("message", handleMessage);
                window.removeEventListener('message', handleMessage);
            };
        }
    }, []);

    // Process user after login
    const processFirebaseUser = async (user) => {
        const userRef = ref(database, "Users/" + user.uid);
        const snapshot = await get(userRef);

        if (!snapshot.exists()) {
            navigate(ROUTES.SIGN_UP, {
                state: {
                    isGoogleSignUp: true,
                    uid: user.uid,
                    name: user.displayName || "",
                    email: user.email || "",
                    profilePic: user.photoURL || "",
                },
            });
        } else {
            localStorage.setItem("uid", user.uid);
            await refreshUser();
            // ✅ create new sessionId (kicks old session)
            await createSession(user);
            // ✅ save FCM token only for this session
            await saveToken(user);
            // 👇 Send message to React Native app if inside WebView
            if (window.isInApp) {
                window.ReactNativeWebView.postMessage(
                    JSON.stringify({
                        type: "USER_LOGGED_IN",
                        uid: user.uid,
                        email: user.email,
                    })
                );
            }
            navigate(ROUTES.DASHBOARD, { replace: true });
        }
    };


    return (
        <div className="bg-main w-full flex items-center justify-center px-4 sm:px-6 md:px-8 py-12">
            <div className="w-full max-w-md mt-8">
                {/* Header */}
                <div className="text-center mb-8">
                    <h3 className="text-2xl md:text-4xl font-bold dark:text-white text-black mb-2">
                        Sign Up
                    </h3>
                    <p className="text-sm md:text-base dark:text-gray-300 text-gray-700">
                        Create your account by filling in the details below.
                    </p>
                    {googleSignUp && (
                        <div className="max-w-md mx-auto mt-4 p-4 bg-blue-900/20 rounded-lg border border-blue-500">
                            <p className="text-blue-300 text-sm">
                                <strong>Almost there!</strong>  You'll need to complete this information to use all features
                            </p>
                        </div>
                    )}
                    {errors.firebase && <p className="text-red-500 text-sm mt-3">{errors.firebase}</p>}
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Name Input */}
                    <div>
                        <label htmlFor="name" className="sr-only">Name</label>
                        <input
                            type="text"
                            id="name"
                            name="name"
                            disabled={googleSignUp && googleProvidedName}
                            value={formData.name}
                            onChange={handleChange}
                            placeholder="Name"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.name ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
                    </div>

                    {/* Email Input */}
                    <div>
                        <label htmlFor="email" className="sr-only">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            disabled={googleSignUp && googleProvidedEmail}
                            value={formData.email}
                            onChange={handleChange}
                            placeholder="Email"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.email ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
                    </div>

                    {/* Password Input */}
                    {
                        !googleSignUp &&
                        <div className="relative">
                            <label htmlFor="password" className="sr-only">Password</label>
                            <input
                                id="password"
                                name="password"
                                type={showPassword ? "text" : "password"}
                                value={formData.password}
                                onChange={handleChange}
                                placeholder="Password"
                                className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 pr-10 ${errors.password ? 'border-red-500' : ''}`}
                                required
                            />
                            <button
                                type="button"
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-black"
                                onClick={togglePasswordVisibility}
                                aria-label={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? (
                                    <FaRegEye size={18} className="text-black" />
                                ) : (
                                    <FaRegEyeSlash size={18} className="text-black" />
                                )}
                            </button>
                            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                        </div>
                    }


                    {/* Username Input */}
                    <div>
                        <label htmlFor="username" className="sr-only">Username</label>
                        <input
                            type="text"
                            id="username"
                            name="username"
                            value={formData.username}
                            onChange={handleChange}
                            placeholder="Username"
                            className={`w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 ${errors.username ? 'border-red-500' : ''}`}
                            required
                        />
                        {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username}</p>}
                    </div>

                    <div>
                        <label htmlFor="username" className="sr-only">About</label>
                        {/* About Textarea */}
                        <textarea
                            name="about"
                            placeholder="About"
                            value={formData.about}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />
                        {errors.about && <p className="text-red-400 text-xs mt-1">{errors.about}</p>}
                    </div>

                    {/* Niche Selector */}
                    <div>
                        <label htmlFor="niche" className="sr-only">Niche</label>
                        <select
                            id="niche"
                            name="niche"
                            value={formData.niche}
                            onChange={handleChange}
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0 appearance-none bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLWNoZXZyb24tZG93biI+PHBhdGggZD0ibTYgOSA2IDYgNi02Ii8+PC9zdmc+')] bg-no-repeat bg-[position:right_0.5rem_center] bg-[size:1.5em_1.5em]"
                        >
                            <option disabled selected>Select your niche</option>
                            <option value="Filmmaker">Filmmaker</option>
                            <option value="Entrepreneur">Entrepreneur</option>
                            <option value="Content Creator">Content Creator</option>
                        </select>
                    </div>
                    {errors.niche && <p className="text-red-400 text-xs mt-1">{errors.niche}</p>}

                    <div>
                        <label htmlFor="country" className="sr-only">Country</label>
                        <Select
                            id="country"
                            name="country"
                            options={countryOptions}
                            value={countryOptions.find(option => option.value === formData.country)}
                            onChange={(selectedOption) =>
                                handleChange({ target: { name: 'country', value: selectedOption.value } })
                            }
                            placeholder="Select your country"
                            isSearchable
                            styles={customSelectStyles}
                        />
                        {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                    </div>


                    {/* Location Input */}
                    <div>
                        <label htmlFor="location" className="sr-only">Location</label>
                        <input
                            type="text"
                            id="location"
                            name="location"
                            value={formData.location}
                            onChange={handleChange}
                            placeholder="Location"
                            className="w-full rounded-xl px-4 py-3 outline-none placeholder:text-[#7C8BA0] 
               bg-[#F5F9FE] dark:bg-white 
               text-black dark:text-black
               border border-gray-200 dark:border-0"
                        />
                    </div>
                    {errors.location && <p className="text-red-400 text-xs mt-1">{errors.location}</p>}

                    {/* Terms Checkbox */}
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="agreeToTerms"
                            name="agreeToTerms"
                            checked={formData.agreeToTerms}
                            onChange={handleChange}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="agreeToTerms" className="ml-2 text-sm dark:text-gray-300 text-gray-700">
                            I agree to the <a href="https://rentry.co/terms-of-service-zylen" target="_blank" className="text-primary" rel="noreferrer">Terms of Service</a> and <a href="https://www.termsfeed.com/live/bf8eefa4-d69e-4ed8-8e42-ae42fedc5081" target="_blank" className="text-primary" rel="noreferrer">Privacy Policy</a>
                        </label>
                    </div>
                    {errors.agreeToTerms && <p className="text-red-400 text-xs">{errors.agreeToTerms}</p>}
                    {/* Login Button */}
                    <button
                        type="submit"
                        className="btn-primary w-full"
                    >
                        {isSubmitting ? "Creating..." : "Create Account"}
                    </button>
                </form>

                {/* Social Login */}
                <div className="mt-6 flex flex-col gap-3">
                    <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                            <div className="w-full border-t border-gray-700"></div>
                        </div>
                        <div className="relative flex justify-center text-sm">
                            <span className="px-2 bg-main text-gray-400">
                                Or
                            </span>
                        </div>
                    </div>


                    <div className="mt-3">

                        {/* Google Login */}
                        <button
                            type="button"
                            className="btn-secondary w-full flex items-center justify-center"
                            onClick={handleGoogleSignUp}
                        >
                            <span className="sr-only">Sign Up with Google</span>
                            <img src={ICONS.google} alt="Google" className="h-5 w-5" />
                            <span className="ml-2 text-light-purple">Sign Up with Google</span>
                        </button>

                    </div>
                </div>

                {/* Sign Up Link */}
                <div className="mt-3 text-center">
                    <p className="dark:text-white text-[16px] text-gray-700">
                        Already have an account?{' '}
                        <button
                            onClick={() => navigate(ROUTES.SIGN_IN)}
                            className="text-primary"
                        >
                            Sign In
                        </button>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SignUp;